import React, { SyntheticEvent } from 'react';
import cc from 'classcat';
import Icon from '../../atoms/icon';
import Text from '../../atoms/text';
import {
  faExternalLinkSquareAlt,
  faChartLine
} from '@fortawesome/pro-solid-svg-icons';
import AnchorButton from '../../molecules/button/anchor';
import { IPatient } from '../../../libs/patient';
import { useActiveDialogsUpdater } from '../../../contexts/active-dialogs';
import { TriageCategory } from '../../../contexts/triage/types';
import StatusIndicator from '../status/status-indicator';
import { AppKey } from '../../../libs/apps';
import { useApps } from '../../../contexts/apps';

/**
 * This action kicks off the
 * view patient flow
 */

interface ViewPatientActionProps {
  patient: IPatient;
  className?: string;
  bare?: boolean;
  category?: TriageCategory;
}

export const ViewPatientAction = ({
  patient,
  className,
  category,
  bare = false
}: ViewPatientActionProps): JSX.Element => {
  const addDialog = useActiveDialogsUpdater();

  const handleClick = (evt: SyntheticEvent) => {
    evt.stopPropagation();
    addDialog({
      id: `viewPatient${patient.id}`,
      urlParam: `patientId=${patient.id}`,
      name: 'ViewPatient',
      payload: {
        patientId: patient.id
      }
    });
  };

  return (
    <AnchorButton
      className={className}
      onClick={handleClick}
      bare={bare}
      variant="minimal"
    >
      {category ? (
        <StatusIndicator category={category} isPatient title={false} />
      ) : null}
      <Text className="font-serif dotted font-bold">
        <span role="status">{`${patient.firstName} ${patient.lastName}`}</span>
      </Text>
      <Icon
        className="mx-2"
        icon={faExternalLinkSquareAlt}
        title={`${patient.firstName} ${patient.lastName}`}
      />
    </AnchorButton>
  );
};

/**
 * This action kicks off the
 * view patient program flow
 */

interface ViewPatientProgramActionProps {
  patient: IPatient;
  appKey: AppKey;
  category?: TriageCategory;
  small?: boolean;
  hideLabel?: boolean;
}

const narrowTextStyles = {
  maxWidth: '120px',
  overflow: 'hidden',
  display: 'inline-block',
  textOverflow: 'ellipsis',
  lineHeight: 1
};

export const ViewPatientProgramAction = ({
  patient,
  appKey,
  category,
  small = false,
  hideLabel = false
}: ViewPatientProgramActionProps): JSX.Element => {
  const { apps: appConfigs } = useApps().data || {};
  const addDialog = useActiveDialogsUpdater();
  const iconClasses = cc(['ml-2', small ? undefined : 'mr-2']);
  const buttonSize = small ? 'small' : 'medium';
  const appName = appConfigs && appKey ? appConfigs[appKey].name : null;

  const handleClick = () => {
    addDialog({
      id: `viewPatient${patient.id}`,
      urlParam: `patientId=${patient.id}&patientTab=${appKey}`,
      name: 'ViewPatient',
      payload: {
        patientId: patient.id,
        tabId: appKey
      }
    });
  };

  /**
   * If there isn't an app name, then
   * it doesn't make sense to render this
   * action.
   */
  if (!appName) {
    return null;
  }

  return (
    <AnchorButton onClick={handleClick} size={buttonSize} variant="minimal">
      {category ? (
        <StatusIndicator
          category={category}
          isPatient={false}
          title={false}
          minimal
        />
      ) : null}
      <Text className="text-small dotted">
        <span
          role="status"
          style={hideLabel && appKey ? narrowTextStyles : null}
        >
          {appName}
        </span>
      </Text>
      <Icon className={iconClasses} icon={faChartLine} title={appName} />
    </AnchorButton>
  );
};
